import {
  getStorage,
  saveStorage,
} from "../../components/composables/localStorage";
import {
  ensureFourTeamNames,
  normalizeSetupName,
} from "../../components/composables/setupFlowHelpers";
import {
  completeLoadMeta,
  createIdleLoadMeta,
  failLoadMeta,
  startLoadMeta,
} from "../../services/firebase/loadState";
import {
  createCardClubRecord,
  deleteCardClubRecord,
  updateCardClubRecord,
  updateSelectedClub,
} from "../../services/firebase/firestoreDataLayer";

const cardClubsModule = {
  namespaced: true,
  state() {
    return {
      //Loaded from database (page mount). Holds list of card clubs displayed on page
      cardClubs: [
        {
          teams: [
            { name: "Team One" },
            { name: "Team Two" },
            { name: "Team Three" },
            { name: "Team Four" },
          ],
        },
      ],

      /* Set to true when data is finished loading from DB.  Monitored by
      watchers to drive next steps in loading process.  Note change from true to false will also trigger watchers */
      cardClubsDataLoaded: false,

      /* Holds the ID of the currently selected card club. */
      cardClubID: null,

      /* Save last game selected on load so gamesData can select the proper game */
      lastGameSelected: null,
      loadMeta: createIdleLoadMeta(),
    };
  },

  getters: {
    //Getters for Card Clubs

    //Return the card clubs array
    cardClubs(state) {
      return state.cardClubs;
    },

    //Returns the selected card club object
    selectedCardClub(state) {
      let ccSelected = state.cardClubs.filter(
        (cc) => cc.id === state.cardClubID
      );
      if (ccSelected.length === 0) {
        return "";
      }
      return ccSelected[0];
    },

    //Returns the ID of the selected card club
    cardClubID(state) {
      return state.cardClubID;
    },

    /* Returns cardClubDataLoaded value.  Will be true true if card club data has been loaded from the database */
    cardClubsDataLoaded(state) {
      return state.cardClubsDataLoaded;
    },

    /* Return the name of the currently sellected card club */
    cardClubName(state) {
      let ccSelected = state.cardClubs.filter(
        (cc) => cc.id === state.cardClubID
      );
      if (ccSelected.length === 0) {
        return "";
      }
      return ccSelected[0].name;
    },

    /* Check to see if a club already exists with the name passed in.  Return true if name is a duplicate */
    isCardClubNameDuplicate: (state) => (name) => {
      const normalizedName = normalizeSetupName(name).toUpperCase();
      let ccSelected = state.cardClubs.filter(
        (cc) => normalizeSetupName(cc.name).toUpperCase() === normalizedName
      );
      if (ccSelected.length === 0) {
        return false;
      }
      return true;
    },

    /* Return the team names written in club */
    cardClubTeamNames(state) {
      let ccSelected = state.cardClubs.filter(
        (cc) => cc.id === state.cardClubID
      );
      if (ccSelected.length === 0) {
        return "";
      }

      return ensureFourTeamNames(ccSelected[0].teams, (key) => {
        const fallbackTeamNames = {
          "cpInsert.team1": "Team One",
          "cpInsert.team2": "Team Two",
          "cpInsert.team3": "Team Three",
          "cpInsert.team4": "Team Four",
        };

        return fallbackTeamNames[key] ?? key;
      });
    },

    loadState(state) {
      return state.loadMeta.loadState;
    },

    loadError(state) {
      return state.loadMeta.error;
    },
  },

  mutations: {
    /* Load Card Clubs to display object. Function should be called after Card Clubs are loaded from database */
    loadCardClubs(state, payLoad) {
      //If payload is false, no card clubs were found.  Set card clubs to empty array and return
      if (payLoad === false) {
        state.cardClubs = [];
        state.cardClubID = null;
        state.cardClubsDataLoaded = true;
        return;
      }

      //Sort card club array by club name
      payLoad.ccs.sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });

      //Copy card clubs from database to display object
      state.cardClubs = payLoad.ccs;
      //Set the selected card club to the one passed in
      state.cardClubID = payLoad.id;
      //Set flag to indicate card club data has been loaded
      state.cardClubsDataLoaded = true;
    },

    /* Add a Card Club to the display object. Make same changes in display object that were made in database */
    addCardClub(state, cc) {
      //cc is a card club returned from the db.  Add it to the display object
      state.cardClubs.push(cc);
      state.cardClubID = cc.id;

      //Sort card club array by club name
      state.cardClubs.sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });
    },

    /* Delete a Card Club from the display object.  */
    deleteCardClub(state, id) {
      //filter all card clubs that do not match the passed in id
      state.cardClubs = state.cardClubs.filter((cc) => cc.id !== id);
    },

    /* Update a Card Club name and description in the display object  */
    updateCardClub(state, updatedItem) {
      let updateCCItem = state.cardClubs.filter(
        (cc) => cc.id === updatedItem.id
      );
      updateCCItem[0].name = updatedItem.name;
      updateCCItem[0].description = updatedItem.description;
    },

    updateCardClubTeamNames(state, teams) {
      const selectedClub = state.cardClubs.find((cc) => cc.id === state.cardClubID);
      if (!selectedClub) {
        return;
      }

      selectedClub.teams = teams;
    },

    /* Update the selected status of a Card Club in the display object */
    setcardClubID(state, id) {
      //For each card club in the display object, set the selected status to false except for the one passed in
      state.cardClubs.forEach((cc) => {
        if (cc.id === id) {
          cc.selected = true;
        } else {
          cc.selected = false;
        }
      });

      //Set the selected card club to the one passed in
      state.cardClubID = id;
    },

    /* Update the last game selected field in the display object */
    updateLastGameSelected(state, payLoad) {
      //If payload is false, no card clubs were found so return
      if (payLoad === false) {
        return;
      }
      //
      let updateCCItem = state.cardClubs.filter((cc) => cc.id === payLoad.ccID);
      updateCCItem[0].lastGameSelected = payLoad.cgID;
    },

    cardClubsLoadStarted(state, requestKey) {
      state.loadMeta = startLoadMeta(requestKey);
    },

    cardClubsLoadSucceeded(state, result) {
      state.loadMeta = completeLoadMeta(state.loadMeta, result);
    },

    cardClubsLoadFailed(state, error) {
      state.loadMeta = failLoadMeta(state.loadMeta, error);
    },

    resetCardClubsState(state) {
      state.cardClubs = [
        {
          teams: [
            { name: "Team One" },
            { name: "Team Two" },
            { name: "Team Three" },
            { name: "Team Four" },
          ],
        },
      ];
      state.cardClubsDataLoaded = false;
      state.cardClubID = null;
      state.lastGameSelected = null;
      state.loadMeta = createIdleLoadMeta();
    },
  },

  /*  Database reads/writes are in actions.  Each database action needs to be reflected in the objects being displayed and so each database action should be paired with a mutation that updates the display object */
  actions: {
    /*Load the card club collection from the database (called when cardclubs.vue is mounted)*/
    async loadCardClubs({ dispatch, rootGetters, rootState }) {
      const idQ = rootGetters["auth/loggedInSiteMemberID"];
      if (
        rootState?.homeContextUserId === idQ &&
        rootState?.homeBootstrap?.loadState === "ready"
      ) {
        return;
      }

      await dispatch("initializeHome", {}, { root: true });
    },

    /* Add a Card Club to the database */
    async addCardClub({ commit, dispatch }, NewCardClub) {
      const result = await createCardClubRecord(NewCardClub);
      if (result.error) {
        throw result.error;
      }

      //Commit the new club to the display object
      commit("addCardClub", result.data);

      //Set the new card club as the selected card club. This keeps no card club from being selected when adding the first card club
      dispatch("updateSelected", result.data.id);
    },

    /* Delete a Card Club from the database, including all players and score cards associated with the club  */
    async deleteCardClub({ commit, dispatch }, id) {
      //Delete all players associated with the club
      await dispatch("cp/deleteAllClubPlayers", id, { root: true });

      //Then delete all score cards associated with the club
      await dispatch("sch/deleteAllScoreCards", id, { root: true });

      //Then delete the card club
      const result = await deleteCardClubRecord(id);
      if (result.error) {
        throw result.error;
      }

      //Then delete the card club from the display object
      commit("deleteCardClub", id);

      //Refresh the broader home context so clubs, selections, and scorecard lists stay aligned.
      await dispatch("refreshHomeContext", null, { root: true });
    },

    /* Update name and/or description of a card club in the database */
    async updateCardClub({ commit }, updatedItem) {
      //to be more responsive, update display array before changing database
      commit("updateCardClub", updatedItem);

      //Get reference to item being updated, then update with data passed in
      const result = await updateCardClubRecord(updatedItem.id, {
        name: updatedItem.name,
        description: updatedItem.description,
      });
      if (result.error) {
        throw result.error;
      }
    },

    /* Update the Selected status of a Card Club in the database.  Make the passed in club selected, make sure all others are not selected */
    async updateSelected({ commit, state }, id) {
      // If no card clubs exist, return
      if (state.cardClubs.length === 0) {
        return;
      }

      // Update display array before changing database
      commit("setcardClubID", id);
      const result = await updateSelectedClub(id);
      if (result.error) {
        throw result.error;
      }
    },

    /* Update the lastGameSelected field.  This function will be called 
    when the selected card game is changed  */
    async updateLastGameSelected({ commit, state }, payLoad) {
      // If no card clubs exist, return
      if (state.cardClubs.length === 0) {
        payLoad = false;
        commit("updateLastGameSelected", payLoad);
        return;
      }

      //Get reference to item being updated, then update with game id passed in
      const result = await updateCardClubRecord(payLoad.ccID, {
        lastGameSelected: payLoad.cgID,
      });
      if (result.error) {
        throw result.error;
      }

      //update the display object
      commit("updateLastGameSelected", payLoad);
    },

    /* Add/update the teams names associated with the active club.  Team names will be written when a new score card is created or any changes are made to the team names*/
    async writeTeamNames({ state, commit }, payload) {
      //Get team names from local storage.  If local storage does not exist, create it
      let savedTeamNames = [];
      savedTeamNames = JSON.parse(getStorage("teamNames", "name"));
      const fallbackTeamNames = [
        "Team One",
        "Team Two",
        "Team Three",
        "Team Four",
      ];

      if (savedTeamNames === null || savedTeamNames === undefined) {
        savedTeamNames = [];
      }

      //Fill in saved teams names if needed to make 4
      if (savedTeamNames != null || savedTeamNames != undefined) {
        for (let i = savedTeamNames.length; i < 4; i++) {
          savedTeamNames.push({ name: fallbackTeamNames[i] });
        }

        saveStorage("teamNames", savedTeamNames);
      }

      payload = ensureFourTeamNames(payload, (key) => {
        const translatedFallbackTeamNames = {
          "cpInsert.team1": "Team One",
          "cpInsert.team2": "Team Two",
          "cpInsert.team3": "Team Three",
          "cpInsert.team4": "Team Four",
        };

        return translatedFallbackTeamNames[key] ?? key;
      });

      //Fill in payload if needed to make 4.  Playload will have the team names that are being used by the score card being created.  This could be 2, 3, 4 teams.  If the score card does not use teams, payload will have one unused team
      if (payload.length === 3) {
        payload.push(savedTeamNames[3]);
      }
      if (payload.length === 2) {
        payload.push(savedTeamNames[2]);
        payload.push(savedTeamNames[3]);
      }
      if (payload.length === 1) {
        payload.push(savedTeamNames[1]);
        payload.push(savedTeamNames[2]);
        payload.push(savedTeamNames[3]);
      }

      commit("updateCardClubTeamNames", payload);

      //Get reference to item being updated, then update with data passed in
      const result = await updateCardClubRecord(state.cardClubID, {
        teams: payload,
      });
      if (result.error) {
        throw result.error;
      }
    },
  },
};

export default cardClubsModule;
